import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Suspense, useCallback, useState } from "react";
import { z } from "zod";

import { ErrorBoundary } from "@/components/error-boundary";
import { saveMbcDraft } from "@/features/request/mbc-chat/lib/draft-api";
import { MBCReview } from "@/features/request/mbc-review";
import { requireMbcFeatureFlag } from "@/lib/posthog-flags";

const mbcReviewSearchSchema = z.object({
  brandId: z.coerce.number().optional(),
  designType: z.coerce.number().optional(),
  ["project-id"]: z.coerce.number().optional(),
  resourceId: z.string().optional(),
  threadId: z.string().optional(),
});

export const Route = createFileRoute("/_auth/_app-shell/requests/mbc-review")({
  beforeLoad: requireMbcFeatureFlag,
  component: RouteComponent,
  validateSearch: mbcReviewSearchSchema,
});

function RouteComponent() {
  const navigate = useNavigate();
  const {
    brandId,
    designType,
    ["project-id"]: projectId,
    resourceId,
    threadId,
  } = Route.useSearch();

  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const handleBack = useCallback(() => {
    void navigate({
      search: {
        brandId,
        designType,
        ["project-id"]: projectId,
        resourceId,
        threadId,
      },
      to: "/requests/mbc",
    });
  }, [brandId, designType, navigate, projectId, resourceId, threadId]);

  // The request is no longer a draft once submitted, so the MBC http have
  // nothing left to show for it. There is no request-detail route in this app
  // (it still lives in the legacy portal), so the list is where this lands.
  const handleSubmitted = useCallback(() => {
    void navigate({ to: "/requests" });
  }, [navigate]);

  const handleSaveToDrafts = useCallback(
    async (title: null | string) => {
      // T8 already created the request when the conversation started — this
      // only PATCHes it and re-saves the Mongo-side brief, it never creates
      // one (D5, MRB-04). Without a thread there is no request to update.
      if (!projectId || !threadId) return;
      setIsSavingDraft(true);
      try {
        await saveMbcDraft({
          brandId,
          designType,
          projectId,
          resourceId,
          threadId,
          title,
        });

        void navigate({ to: "/requests" });
      } finally {
        setIsSavingDraft(false);
      }
    },
    [brandId, designType, navigate, projectId, resourceId, threadId],
  );

  return (
    <Suspense>
      <ErrorBoundary>
        <MBCReview
          brandId={brandId}
          designTypeId={designType}
          isSavingDraft={isSavingDraft}
          onBack={handleBack}
          onSaveToDrafts={(title) => void handleSaveToDrafts(title)}
          onSubmitted={handleSubmitted}
          resourceId={resourceId}
          threadId={threadId}
        />
      </ErrorBoundary>
    </Suspense>
  );
}
